import { Button, Chip } from '@mui/material'
import type { SxProps } from '@mui/material'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'

export interface WacomStatusChipProps {
  supported: boolean
  ready: boolean
  connecting: boolean
  onConnect: () => void
  /** Optional model name to show in the connected state (e.g. "STU-430"). */
  deviceName?: string | null
  sx?: SxProps
}

/**
 * Status indicator for the signature pad. Wording is intentionally vendor-
 * neutral ("เครื่องเซ็น") so the same control reads naturally regardless of
 * the underlying hardware brand.
 *
 * - Browser without WebHID: renders nothing.
 * - Supported but not connected: small outlined "เชื่อมต่อเครื่องเซ็น" button.
 * - Connected: compact green chip "เครื่องเซ็น · พร้อมใช้งาน".
 */
export default function WacomStatusChip(props: WacomStatusChipProps) {
  if (!props.supported) return null

  if (props.ready) {
    const name = (props.deviceName && props.deviceName.trim()) || 'เครื่องเซ็น'
    return (
      <Chip
        size="small"
        variant="outlined"
        icon={<CheckCircleRoundedIcon sx={{ fontSize: 16 }} />}
        label={`${name} · พร้อมใช้งาน`}
        sx={{
          ml: 1,
          height: 28,
          borderRadius: '4px',
          borderColor: 'success.main',
          color: 'success.dark',
          bgcolor: 'rgba(46, 125, 50, 0.08)',
          fontWeight: 500,
          '& .MuiChip-icon': { color: 'success.main', ml: '6px' },
          '& .MuiChip-label': { px: 1 },
          ...props.sx,
        }}
      />
    )
  }

  return (
    <Button
      variant="outlined"
      size="small"
      onClick={props.onConnect}
      disabled={props.connecting}
      sx={{
        ml: 1,
        textTransform: 'none',
        fontWeight: 500,
        borderRadius: '4px',
        borderColor: 'divider',
        color: 'text.primary',
        '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(25, 118, 210, 0.04)' },
        ...props.sx,
      }}
    >
      {props.connecting ? 'กำลังเชื่อมต่อ…' : 'เชื่อมต่อเครื่องเซ็น'}
    </Button>
  )
}
